import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Circle } from 'react-native-svg';
import { useStrategyOrganizationsQuery } from '../../hooks/queries';
import { OrganizationList } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { LoadingScreen, PageContainer, Navbar, Breadcrumb } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useRoleNavigation } from '../../hooks';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useYear } from '../../contexts/YearContext';
import { formatPct } from '../../utils/formatPercentage';
import { deviationColor } from '../../utils/metricColors';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// Performance Deviation Circle Component
// ============================================================

interface DeviationCircleProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

const DeviationCircle = ({ percentage, size = 120, strokeWidth = 10 }: DeviationCircleProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const safePercentage = Math.min(Math.max(percentage, 0), 100);
  const strokeDashoffset = circumference - (safePercentage / 100) * circumference;

  // Use shared deviation thresholds (≤20 green, ≤50 orange, >50 red) so this
  // matches the deviation gauges on the strategy / strategy-organizations cards.
  const color = deviationColor(safePercentage);

  // Calculate indicator dot position at the end of the arc
  const angle = ((safePercentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = size / 2 + radius * Math.cos(angle);
  const dotY = size / 2 + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle - translucent same color as progress arc */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={`${color}33`}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        {/* Indicator dot at end of arc */}
        {safePercentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={color}
            stroke="#FFFFFF"
            strokeWidth={2}
          />
        )}
      </Svg>
      {/* Percentage text */}
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={[styles.percentageText, { color }]}>
          %{formatPct(safePercentage)}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Chevron Icon
// ============================================================

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.5" fill="none" />
  </Svg>
);

// ============================================================
// Main Component
// ============================================================

export default function PerformanceDeviationScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { width } = useWindowDimensions();
  const isWideScreen = width >= 1024;
  const { navigateToHome } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    orgName: colors.textSecondary,
    filterDropdownBg: colors.card,
    filterDropdownBorder: colors.border,
    filterDropdownText: colors.textSecondary,
    filterButtonBg: isDark ? colors.backgroundAlt : '#F3F4F6',
    filterButtonText: colors.textSecondary,
    emptyText: colors.textMuted,
    circleTrack: isDark ? '#4B5563' : '#E5E7EB',
  };

  const [selectedFilter, setSelectedFilter] = useState<string>('all');

  // React Query holds the data — navigation focus and year change both fold
  // into the queryKey, so stale-while-revalidate keeps a warm cache between
  // visits instead of flashing the loading spinner on every focus.
  const {
    data: organizations = [],
    isPending: loading,
    isRefetching: refreshing,
    refetch,
  } = useStrategyOrganizationsQuery(selectedYear);

  // Deviation matches the org cards on /strategy and /strategy-organizations:
  // 100 - commitment_index, clamped to [0, 100]. Returns null when the org has
  // no commitment_index so the card can render the "--" placeholder.
  const computeDeviation = (org: OrganizationList): number | null => {
    const commitment = org.commitment_index;
    if (commitment == null) return null;
    return Math.max(0, Math.min(100, 100 - Number(commitment)));
  };

  // Sort by deviation descending (highest deviation first). Memoized so a
  // parent re-render (filter change, language toggle) doesn't re-sort N orgs.
  const sortedOrganizations = useMemo(
    () => [...organizations].sort(
      (a, b) => (computeDeviation(b) ?? 0) - (computeDeviation(a) ?? 0),
    ),
    [organizations],
  );

  const onRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Handle card press - navigate to organization details
  const handleCardPress = (org: OrganizationList) => {
    // Navigate to Goals screen filtered by organization
    navigation.navigate('Goals', { organizationId: org.id } as any);
  };

  // Only show the full-screen spinner on genuine first load; subsequent
  // refocuses render instantly from cache.
  if (loading) {
    return <LoadingScreen />;
  }

  // Calculate grid columns based on screen width
  const getGridColumns = () => {
    if (width >= 1200) return 4;
    if (width >= 900) return 3;
    if (width >= 600) return 2;
    return 1;
  };

  const columns = getGridColumns();

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'الرئيسية' },
            { label: 'الانحرافات' },
          ]}
        />

        {/* Page Title */}
        <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>مؤشرات انحراف الأداء في الجهات</Text>

        {/* Filter Section */}
        <View style={styles.filterSection}>
          <View style={styles.filterRow}>
            <TouchableOpacity style={[styles.filterButton, { backgroundColor: themeColors.filterButtonBg }]}>
              <Text style={[styles.filterButtonText, { color: themeColors.filterButtonText }]}>تطبيق الفلتر</Text>
            </TouchableOpacity>

            <TouchableOpacity style={[styles.filterDropdown, { backgroundColor: themeColors.filterDropdownBg, borderColor: themeColors.filterDropdownBorder }]}>
              <ChevronDownIcon size={16} color={colors.iconDefault} />
              <Text style={[styles.filterDropdownText, { color: themeColors.filterDropdownText }]}>جميع الجهات</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Organizations Grid */}
        <View style={[
          styles.cardsGrid,
          { flexDirection: 'row-reverse', flexWrap: 'wrap' }
        ]}>
          {sortedOrganizations.map((org) => {
            const deviation = computeDeviation(org);
            return (
              <TouchableOpacity
                key={org.id}
                style={[
                  styles.orgCard,
                  { width: `${100 / columns - 2}%`, backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }
                ]}
                onPress={() => handleCardPress(org)}
                activeOpacity={0.7}
              >
                {/* Organization Name */}
                <Text style={[styles.orgName, { color: themeColors.orgName }]} numberOfLines={2}>
                  {translateOrg(org.name)}
                </Text>

                {/* Deviation Circle */}
                <View style={styles.circleContainer}>
                  {deviation != null ? (
                    <DeviationCircle
                      percentage={deviation}
                      size={110}
                      strokeWidth={8}
                    />
                  ) : (
                    <View style={{ width: 110, height: 110, justifyContent: 'center', alignItems: 'center' }}>
                      <Text style={{ fontSize: 24, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                    </View>
                  )}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Empty State */}
        {organizations.length === 0 && (
          <View style={styles.emptyState}>
            <Text style={[styles.emptyStateText, { color: themeColors.emptyText }]}>لا توجد بيانات</Text>
          </View>
        )}
      </ScrollView>
    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 40,
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: 8,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#3B82F6',
  },
  breadcrumbSeparator: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    marginHorizontal: 8,
  },
  breadcrumbCurrent: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#A9211F',
  },

  // Page Title
  pageTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#111827',
    textAlign: 'right',
    marginBottom: 24,
  },

  // Filter Section
  filterSection: {
    marginBottom: 24,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
  },
  filterDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    gap: 8,
  },
  filterDropdownText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#374151',
  },
  filterButton: {
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  filterButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#374151',
  },

  // Cards Grid
  cardsGrid: {
    gap: 20,
  },

  // Organization Card
  orgCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 16,
    marginBottom: 20,
    alignItems: 'center',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  orgName: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'center',
    marginBottom: 16,
    minHeight: 40,
  },
  circleContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Percentage Text
  percentageText: {
    fontSize: 22,
    fontFamily: FONTS.numbers,
    fontWeight: '600',
  },

  // Empty State
  emptyState: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
});
